import React, { useState, useEffect } from 'react';
import './MissingPersonsPage.css';

const MissingPersonsPage = () => {
  // 상태 관리
  const [missingPersons, setMissingPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    sexdstnDscd: '',
    occrde: '',
    includeImages: true
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);

  // 백엔드 서버 URL
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  // 검색 파라미터 변경 핸들러
  const handleSearchParamsChange = (newParams) => {
    setSearchParams({ ...searchParams, ...newParams });
  };

  // 검색 실행 핸들러
  const handleSearch = () => {
    fetchMissingPersons();
  };

  // 초기화 핸들러
  const handleReset = () => {
    setSearchParams({
      keyword: '',
      sexdstnDscd: '',
      occrde: '',
      includeImages: true
    });
    // 초기화 후 바로 데이터를 다시 불러오기 위해 setTimeout 사용
    setTimeout(() => {
      fetchMissingPersons();
    }, 100);
  };

  // API 형식으로 날짜 변환 (YYYY-MM-DD -> YYYYMMDD)
  const formatDateForApi = (htmlDate) => {
    if (!htmlDate) return '';
    return htmlDate.replace(/-/g, '');
  };

  // HTML 날짜 입력 형식으로 변환 (YYYYMMDD -> YYYY-MM-DD)
  const formatDateForInput = (apiDate) => {
    if (!apiDate || apiDate.length !== 8) return '';
    return `${apiDate.substring(0, 4)}-${apiDate.substring(4, 6)}-${apiDate.substring(6, 8)}`;
  };

  // 실종자 상세 정보 표시 핸들러
  const handlePersonClick = (person) => {
    console.log('실종자 카드 클릭:', person);
    setSelectedPerson(person);
  };

  // 상세 정보 모달 닫기 핸들러
  const handleCloseDetails = () => {
    console.log('모달 닫기 클릭됨');
    setSelectedPerson(null);
  };

  // 실종자 데이터 가져오기
  const fetchMissingPersons = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('검색 파라미터:', searchParams);

      // 통합 API 사용 - 여러 소스의 데이터를 결합
      const params = new URLSearchParams({
        keyword: searchParams.keyword || '',
        sexdstnDscd: searchParams.sexdstnDscd,
        occrde: searchParams.occrde || '',
        includeImages: searchParams.includeImages ? 'true' : 'false'
      });

      // API 호출
      const response = await fetch(`${API_URL}/api/missing-persons-combined?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      // 응답 상태 확인
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: '알 수 없는 오류' }));
        if (errorData.details && errorData.details.includes('1일 한도 호출 횟수를 초과')) {
          throw new Error('1일 한도 호출 횟수를 초과하였습니다.');
        }
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('API 응답:', data);

      if (data.result === '00') {
        // 데이터 정렬 - 최신순 (occrde 기준 내림차순)
        const sortedData = data.list.sort((a, b) => {
          if (!a.occrde) return 1;
          if (!b.occrde) return -1;
          return parseInt(b.occrde, 10) - parseInt(a.occrde, 10);
        });

        setMissingPersons(sortedData);
        setLastUpdated(new Date());
        setError(null);

        // 결과 수 로깅
        console.log(`총 ${sortedData.length}명의 실종자 데이터를 가져왔습니다.`);
        console.log('디버그 정보:', data.debug);
      } else {
        setError(data.msg || '데이터를 불러오는 데 실패했습니다.');
        setMissingPersons([]);
      }
    } catch (err) {
      console.error('실종자 데이터 로딩 오류:', err);

      // API 한도 초과 에러 확인
      if (err.message && err.message.includes('1일 한도 호출 횟수를 초과')) {
        setError('API_LIMIT_EXCEEDED');
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
      } else {
        setError('실종자 정보를 불러오는 데 실패했습니다: ' + (err.message || '알 수 없는 오류'));
      }
      setMissingPersons([]);
    } finally {
      setLoading(false);
    }
  };

  // 이미지 URL 프록시 처리 (CORS 문제 해결)
  const getProxyImageUrl = (originalUrl) => {
    if (!originalUrl) return '';

    // 이미 데이터 URL 또는 Base64인 경우 그대로 반환
    if (originalUrl.startsWith('data:image/') ||
      originalUrl.startsWith('/9j/') ||
      originalUrl.startsWith('iVBOR')) {
      return originalUrl;
    }

    // 이미지 프록시 URL 반환
    return `${API_URL}/api/image-proxy?url=${encodeURIComponent(originalUrl)}`;
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchMissingPersons();

    // 5분마다 자동 갱신
    // const interval = setInterval(() => {
    //   console.log('실종자 데이터 자동 갱신');
    //   fetchMissingPersons();
    // }, 5 * 60 * 1000); // 5분

    // return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 날짜 형식 변환 함수
  const formatDate = (dateStr) => {
    if (!dateStr) return '정보 없음';

    // YYYYMMDD 형식을 YYYY-MM-DD로 변환
    if (dateStr.length === 8) {
      return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
    }

    // 이미 정상적인 날짜 형식이면 그대로 반환
    return dateStr;
  };

  // 성별 표시 함수
  const formatGender = (genderCode) => {
    if (genderCode === null || genderCode === undefined || genderCode === '') {
      return '성별 미상';
    }

    // 숫자 타입 처리
    if (typeof genderCode === 'number') {
      return genderCode === 1 ? '남자' : genderCode === 2 ? '여자' : '성별 미상';
    }

    // 문자열로 변환
    const code = String(genderCode).trim().toUpperCase();

    // API 성별 코드에 따른 처리
    if (code === '1' || code === 'M') return '남자';
    if (code === '2' || code === 'F') return '여자';
    if (code === '남자' || code === '남성') return '남자';
    if (code === '여자' || code === '여성') return '여자';

    return '성별 미상';
  };

  return (
    <div className="missing-persons-page">
      <div className="page-header">
        <h1 className="page-title">울산 지역 실종자 찾기</h1>
      </div>

      {/* 검색 패널 */}
      <div className="search-panel">
        <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="search-form">
          <div className="search-row">
            <div className="form-group">
              <label htmlFor="keyword">이름/지역:</label>
              <input
                type="text"
                id="keyword"
                name="keyword"
                value={searchParams.keyword}
                onChange={(e) => handleSearchParamsChange({ keyword: e.target.value })}
                placeholder="이름 또는 세부 지역"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sexdstnDscd">성별:</label>
              <select
                id="sexdstnDscd"
                name="sexdstnDscd"
                value={searchParams.sexdstnDscd}
                onChange={(e) => handleSearchParamsChange({ sexdstnDscd: e.target.value })}
                className="form-control"
              >
                <option value="">전체</option>
                <option value="M">남자</option>
                <option value="F">여자</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="occrde">실종일자:</label>
              <input
                type="date"
                id="occrde"
                name="occrde"
                value={formatDateForInput(searchParams.occrde)}
                onChange={(e) => {
                  const htmlDate = e.target.value;
                  handleSearchParamsChange({
                    occrde: htmlDate ? formatDateForApi(htmlDate) : ''
                  });
                }}
                className="form-control"
              />
            </div>
          </div>

          <div className="search-row">
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="includeImages"
                name="includeImages"
                checked={searchParams.includeImages}
                onChange={(e) => handleSearchParamsChange({ includeImages: e.target.checked })}
              />
              <label htmlFor="includeImages">이미지 포함</label>
            </div>

            <div className="button-group">
              <button type="submit" className="search-button">검색</button>
              <button type="button" className="reset-button" onClick={handleReset}>초기화</button>
            </div>
          </div>
        </form>
      </div>

      <div className="controls-bar">
        <button onClick={fetchMissingPersons} className="refresh-button" disabled={loading}>
          {loading ? '로딩 중...' : '새로고침'}
        </button>

        <div className="info-text">
          {missingPersons.length > 0 ? `${missingPersons.length}건의 실종자 정보 • ` : ''}
          최신순 정렬
        </div>

        {lastUpdated && (
          <div className="last-updated-text">
            마지막 업데이트: {lastUpdated.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* 에러 메시지 표시 */}
      {error && (
        error === 'API_LIMIT_EXCEEDED' ? (
          <div className="api-limit-message">
            <h3>⚠️ API 호출 한도 초과</h3>
            <p>죄송합니다. 오늘 API 호출 한도를 초과했습니다.</p>
            <p>내일 다시 시도해주시거나, 관리자에게 문의해주세요.</p>
            <p>🕐 오늘 자정 이후에 다시 정상적으로 이용 가능합니다.</p>
          </div>
        ) : (
          <div className="error-message">{error}</div>
        )
      )}

      {/* 실종자 목록 */}
      {loading && missingPersons.length === 0 ? (
        <div className="loading">실종자 정보를 불러오는 중...</div>
      ) : missingPersons.length === 0 ? (
        <div className="no-results">
          <h3>검색 결과가 없습니다</h3>
          <p>다른 검색어로 다시 시도하거나 검색 조건을 변경해 보세요.</p>
        </div>
      ) : (
        <div className="persons-grid">
          {missingPersons.map((person) => (
            <div
              key={person.occrId || person.id || Math.random()}
              className="person-card"
              onClick={() => handlePersonClick(person)}
              style={{ cursor: 'pointer' }}
            >
              <div className="person-card-image">
                {person.tknphotoFile ? (
                  <img
                    src={getProxyImageUrl(person.tknphotoFile)}
                    alt={person.nm || '실종자'}
                    className="person-image"
                    onError={(e) => {
                      console.log('이미지 로드 실패:', person.tknphotoFile);
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/150x200?text=이미지+없음';
                    }}
                  />
                ) : (
                  <div className="no-image-placeholder">
                    <div className="placeholder-content">
                      <span className="gender-icon">
                        {formatGender(person.sexdstnDscd) === '남자' ? '♂' :
                          formatGender(person.sexdstnDscd) === '여자' ? '♀' : '?'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="person-card-content">
                <h3 className="person-name">{person.nm || '실종자'}</h3>

                <div className="person-info">
                  <p>
                    <strong>{formatGender(person.sexdstnDscd)}</strong>
                    {person.age && `, ${person.age}세`}
                  </p>
                  <p>
                    <strong>실종일:</strong> {formatDate(person.occrde)}
                  </p>
                  <p>
                    <strong>장소:</strong> {person.occrAdres || '정보 없음'}
                  </p>
                  {person.etcSpfeatr && (
                    <p className="person-feature">
                      <strong>특징:</strong> {person.etcSpfeatr.length > 30 ?
                        `${person.etcSpfeatr.substring(0, 30)}...` :
                        person.etcSpfeatr}
                    </p>
                  )}
                </div>

                <div
                  className="view-details"
                  onClick={(e) => {
                    e.stopPropagation(); // 이벤트 버블링 방지
                    handlePersonClick(person);
                  }}
                >
                  자세히 보기 <i className="fas fa-chevron-right"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 실종자 상세 정보 모달 */}
      {selectedPerson && (
        <div className="person-details-modal">
          <div className="modal-overlay" onClick={handleCloseDetails}></div>
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">실종자 상세 정보</h3>
              <button className="close-button" onClick={handleCloseDetails}>×</button>
            </div>

            <div className="modal-body">
              <div className="modal-info-grid">
                {/* 상단 헤더 섹션 */}
                <div className="person-header-section">
                  <div className="person-image-container">
                    {selectedPerson.tknphotoFile ? (
                      <img
                        src={getProxyImageUrl(selectedPerson.tknphotoFile)}
                        alt={selectedPerson.nm || '실종자'}
                        className="person-detail-image"
                        onError={(e) => {
                          console.log('상세 이미지 로드 실패:', selectedPerson.tknphotoFile);
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/150x200?text=이미지+없음';
                        }}
                      />
                    ) : (
                      <div className="no-image-placeholder">
                        <div className="placeholder-content large">
                          <span className="gender-icon large">
                            {formatGender(selectedPerson.sexdstnDscd) === '남자' ? '♂' :
                              formatGender(selectedPerson.sexdstnDscd) === '여자' ? '♀' : '?'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="person-basic-info">
                    <h2 className="person-name-large">{selectedPerson.nm || '이름 미상'}</h2>
                    <div className="person-basic-details">
                      <div className="basic-detail-item">
                        <span className="detail-icon">👤</span>
                        <span>{formatGender(selectedPerson.sexdstnDscd)}</span>
                      </div>
                      {selectedPerson.age && (
                        <div className="basic-detail-item">
                          <span className="detail-icon">🎂</span>
                          <span>{selectedPerson.age}세</span>
                        </div>
                      )}
                      <div className="basic-detail-item">
                        <span className="detail-icon">📅</span>
                        <span>{formatDate(selectedPerson.occrde)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 정보 섹션들 */}
                <div className="info-sections">
                  {/* 기본 정보 */}
                  <div className="info-section">
                    <div className="info-section-header">
                      <span className="section-icon">📋</span>
                      <h3 className="section-title">기본 정보</h3>
                    </div>
                    <div className="info-items">
                      <div className="info-item">
                        <span className="info-item-icon">📍</span>
                        <div className="info-item-content">
                          <div className="info-item-label">실종 장소</div>
                          <div className="info-item-value">{selectedPerson.occrAdres || '정보 없음'}</div>
                        </div>
                      </div>
                      <div className="info-item">
                        <span className="info-item-icon">📞</span>
                        <div className="info-item-content">
                          <div className="info-item-label">신고 일자</div>
                          <div className="info-item-value">{formatDate(selectedPerson.writngDe)}</div>
                        </div>
                      </div>
                      {selectedPerson.ageNow && selectedPerson.age && selectedPerson.ageNow !== selectedPerson.age && (
                        <div className="info-item">
                          <span className="info-item-icon">🔄</span>
                          <div className="info-item-content">
                            <div className="info-item-label">현재 추정 나이</div>
                            <div className="info-item-value">{selectedPerson.ageNow}세</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 신체 특징 */}
                  <div className="info-section">
                    <div className="info-section-header">
                      <span className="section-icon">🏃</span>
                      <h3 className="section-title">신체 특징</h3>
                    </div>
                    <div className="info-items">
                      <div className="info-item">
                        <span className="info-item-icon">📏</span>
                        <div className="info-item-content">
                          <div className="info-item-label">키</div>
                          <div className={`info-item-value ${!selectedPerson.height ? 'no-data' : ''}`}>
                            {selectedPerson.height ? `${selectedPerson.height}cm` : '정보 없음'}
                          </div>
                        </div>
                      </div>
                      <div className="info-item">
                        <span className="info-item-icon">⚖️</span>
                        <div className="info-item-content">
                          <div className="info-item-label">체중</div>
                          <div className={`info-item-value ${!selectedPerson.bdwgh ? 'no-data' : ''}`}>
                            {selectedPerson.bdwgh ? `${selectedPerson.bdwgh}kg` : '정보 없음'}
                          </div>
                        </div>
                      </div>
                      <div className="info-item">
                        <span className="info-item-icon">🧍</span>
                        <div className="info-item-content">
                          <div className="info-item-label">체형</div>
                          <div className={`info-item-value ${!selectedPerson.frmDscd ? 'no-data' : ''}`}>
                            {selectedPerson.frmDscd || '정보 없음'}
                          </div>
                        </div>
                      </div>
                      <div className="info-item">
                        <span className="info-item-icon">😊</span>
                        <div className="info-item-content">
                          <div className="info-item-label">얼굴형</div>
                          <div className={`info-item-value ${!selectedPerson.faceshpeDscd ? 'no-data' : ''}`}>
                            {selectedPerson.faceshpeDscd || '정보 없음'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 외관 특징 */}
                  <div className="info-section">
                    <div className="info-section-header">
                      <span className="section-icon">👕</span>
                      <h3 className="section-title">외관 특징</h3>
                    </div>
                    <div className="info-items">
                      <div className="info-item">
                        <span className="info-item-icon">👔</span>
                        <div className="info-item-content">
                          <div className="info-item-label">복장</div>
                          <div className={`info-item-value ${!selectedPerson.alldressingDscd ? 'no-data' : ''}`}>
                            {selectedPerson.alldressingDscd || '정보 없음'}
                          </div>
                        </div>
                      </div>
                      <div className="info-item">
                        <span className="info-item-icon">💇</span>
                        <div className="info-item-content">
                          <div className="info-item-label">머리 스타일</div>
                          <div className={`info-item-value ${!selectedPerson.hairshpeDscd ? 'no-data' : ''}`}>
                            {selectedPerson.hairshpeDscd || '정보 없음'}
                          </div>
                        </div>
                      </div>
                      <div className="info-item">
                        <span className="info-item-icon">🎨</span>
                        <div className="info-item-content">
                          <div className="info-item-label">머리 색상</div>
                          <div className={`info-item-value ${!selectedPerson.haircolrDscd ? 'no-data' : ''}`}>
                            {selectedPerson.haircolrDscd || '정보 없음'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 기타 특징 */}
                  {selectedPerson.etcSpfeatr && (
                    <div className="info-section feature-section">
                      <div className="info-section-header">
                        <span className="section-icon">✨</span>
                        <h3 className="section-title">기타 특징</h3>
                      </div>
                      <p className="feature-text">{selectedPerson.etcSpfeatr}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="report-info">
                <h4>신고 정보</h4>
                <div className="report-contacts">
                  <div className="report-contact">
                    <span className="report-contact-icon">📞</span>
                    <div className="report-contact-info">
                      <div className="report-contact-label">실종아동찾기센터</div>
                      <div className="report-contact-number">182</div>
                    </div>
                  </div>
                  <div className="report-contact">
                    <span className="report-contact-icon">🚨</span>
                    <div className="report-contact-info">
                      <div className="report-contact-label">경찰청 신고전화</div>
                      <div className="report-contact-number">112</div>
                    </div>
                  </div>
                </div>
                <p className="report-tip">
                  실종자를 발견하셨다면 즉시 가까운 경찰서나 지구대로 신고해 주세요.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="close-button-footer" onClick={handleCloseDetails}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissingPersonsPage;